// src/finance/TrackingPage.tsx
// Suivi mensuel (spec § 3) : mois réels vs objectif issu de la simulation courante.
import { useMemo, useState } from 'react'
import {
  useFinanceSettings,
  useRecurringExpenses,
  useFinanceMonths,
  useCreateFinanceMonth,
  useUpdateFinanceMonth,
  useDeleteFinanceMonth,
} from './api'
import { computeMonthlyTargets, effectiveTargetsFor, targetsScenarioLabel } from './targets'
import { monthlyAmountOf, prefillForMonth } from './expenseMath'
import { MonthEditor, formatMonthLabel, cumulBeforeMonth } from './components/MonthEditor'
import { YearTable } from './components/YearTable'
import { fmtEur } from './format'

export function TrackingPage() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const settingsQuery = useFinanceSettings()
  const expensesQuery = useRecurringExpenses()
  const monthsQuery = useFinanceMonths(year)
  const createMonth = useCreateFinanceMonth()
  const updateMonth = useUpdateFinanceMonth()
  const deleteMonth = useDeleteFinanceMonth()

  const settings = settingsQuery.data
  // Total MENSUALISÉ (les /an comptent /12) — cohérent avec la simulation.
  const companyTotal = useMemo(
    () =>
      (expensesQuery.data ?? [])
        .filter((e) => e.kind === 'company' && e.active)
        .reduce((sum, e) => sum + monthlyAmountOf(e), 0),
    [expensesQuery.data],
  )
  const targets = useMemo(
    () => (settings ? computeMonthlyTargets(settings, companyTotal) : null),
    [settings, companyTotal],
  )

  // Mois proposé à la création : le mois courant s'il n'est pas saisi, sinon le suivant du dernier.
  const nextMonth = useMemo(() => {
    const taken = new Set((monthsQuery.data ?? []).map((m) => m.month))
    const now = new Date()
    let candidate = `${year}-${String(year === now.getFullYear() ? now.getMonth() + 1 : 1).padStart(2, '0')}`
    let guard = 0
    while (taken.has(candidate) && guard < 12) {
      const [y, m] = candidate.split('-').map(Number)
      const next = new Date(y!, (m ?? 1), 1)   // mois suivant
      candidate = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`
      guard += 1
    }
    return candidate
  }, [monthsQuery.data, year])

  // `nextMonth` n'avance que vers l'avant : adopter l'outil en août rendait janvier→juillet
  // impossibles à créer. Le champ mois est donc librement éditable, `nextMonth` n'en est que
  // la valeur par défaut tant que l'utilisateur n'a rien choisi (null = pas de choix ;
  // vider le champ revient à reprendre le défaut). Un mois déjà saisi ressort en 409 serveur,
  // affiché dans l'encart d'erreur existant.
  const [monthChoice, setMonthChoice] = useState<string | null>(null)
  const selectedMonth = monthChoice ?? nextMonth

  const handleCreate = () => {
    // Préremplissage : mensuelles telles quelles ; annuelles DATÉES du mois créé,
    // plein montant ; annuelles non datées ou d'un autre mois : rien (spec).
    const prefill = prefillForMonth(
      (expensesQuery.data ?? []).filter((e) => e.kind === 'company' && e.active),
      selectedMonth,
    )
    // Objectifs FIGÉS à la création : ils sont calculés ICI, côté client, parce que le
    // moteur (src/finance/engine) est hors de portée de functions/ — la seule frontière
    // functions/ → src/ autorisée est le type-only src/lib/types.ts. Le serveur les
    // valide (bornes ≥ 0, nullables) et les stocke ; ils deviennent immuables ensuite.
    // `unreachable` : rien à figer, on laisse NULL → le mois retombera sur les courants.
    createMonth.mutate({
      month: selectedMonth,
      expenses: prefill,
      targetRevenue: targets && !targets.unreachable ? targets.revenue : null,
      targetNet: targets && !targets.unreachable ? targets.net : null,
      targetExpenses: targets && !targets.unreachable ? targets.expenses : null,
    })
  }

  // Erreur avant chargement (même raison que CalculatorPage : settings/targets restent null).
  // `!data` : un refetch raté après une mutation ne doit pas tuer la page — React Query
  // garde les données déjà chargées, l'édition des mois reste opérante.
  if (
    (settingsQuery.isError && !settingsQuery.data) ||
    (expensesQuery.isError && !expensesQuery.data) ||
    (monthsQuery.isError && !monthsQuery.data)
  ) {
    return <div style={{ padding: '2rem', color: 'var(--arancio)', fontSize: '0.875rem' }}>Erreur de chargement des données Finance.</div>
  }
  if (settingsQuery.isLoading || expensesQuery.isLoading || monthsQuery.isLoading || !settings || !targets) {
    return <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: '0.875rem' }}>Chargement du suivi…</div>
  }

  const months = monthsQuery.data ?? []

  return (
    <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '860px' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--fg)', margin: 0 }}>Suivi mensuel</h1>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            color: 'var(--fg)',
            fontSize: '0.8125rem',
            fontFamily: 'inherit',
            padding: '0.375rem 0.5rem',
          }}
        >
          {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <span style={{ fontSize: '0.75rem', color: targets.unreachable ? 'var(--arancio)' : 'var(--muted)' }}>
          {targets.unreachable
            ? 'Objectif : cible inatteignable avec ces paramètres fiscaux.'
            : `Objectif : ${fmtEur(targets.revenue)} de CA — ${fmtEur(targets.net)} net (${targetsScenarioLabel(settings)})`}
        </span>
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setMonthChoice(e.target.value === '' ? null : e.target.value)}
          aria-label="Mois à ajouter"
          style={{
            marginLeft: 'auto',
            background: 'var(--bg)',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            color: 'var(--fg)',
            fontSize: '0.8125rem',
            fontFamily: 'inherit',
            padding: '0.375rem 0.5rem',
          }}
        />
        <button
          onClick={handleCreate}
          disabled={createMonth.isPending}
          style={{
            background: 'var(--arancio)',
            border: '1px solid var(--arancio-deep)',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '0.8125rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            padding: '0.4375rem 1rem',
            cursor: createMonth.isPending ? 'default' : 'pointer',
          }}
        >
          + Ajouter {formatMonthLabel(selectedMonth)}
        </button>
      </header>

      {createMonth.isError && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--arancio)', border: '1px solid var(--arancio-deep)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
          {createMonth.error.message}
        </div>
      )}

      {/* Sans ces encarts, un PUT/DELETE raté est silencieux : le bouton repasse à « Enregistrer »
          avec les valeurs saisies à l'écran, rien ne distingue enregistré de perdu. */}
      {updateMonth.isError && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--arancio)', border: '1px solid var(--arancio-deep)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
          Échec de l'enregistrement du mois : {updateMonth.error.message}
        </div>
      )}

      {deleteMonth.isError && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--arancio)', border: '1px solid var(--arancio-deep)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
          Échec de la suppression : {deleteMonth.error.message}
        </div>
      )}

      <YearTable months={months} targets={targets} />

      {months.length === 0 && (
        <p style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
          Aucun mois saisi pour {year}. Ajoute ton premier mois — ses dépenses seront préremplies
          depuis tes dépenses société récurrentes.
        </p>
      )}

      {months.map((month) => (
        <MonthEditor
          key={`${month.id}-${month.updatedAt}`}
          month={month}
          // Objectifs FIGÉS du mois s'il en a ; sinon les courants, signalés à l'écran.
          targets={effectiveTargetsFor(month, targets)}
          settings={settings}
          // L'IR se liquide sur l'ANNÉE : chaque mois reçoit le cumul des mois
          // chronologiquement antérieurs pour que son IR soit un différentiel.
          yearToDateBefore={cumulBeforeMonth(months, month.month)}
          saving={updateMonth.isPending}
          onSave={(draft) =>
            updateMonth.mutate({
              id: month.id,
              revenue: draft.revenue,
              salaryPaid: draft.salaryPaid,
              dividendsPaid: draft.dividendsPaid,
              note: draft.note === '' ? null : draft.note,
              expenses: draft.expenses,
            })
          }
          onDelete={() => {
            if (window.confirm(`Supprimer ${formatMonthLabel(month.month)} et ses dépenses ?`)) {
              deleteMonth.mutate(month.id)
            }
          }}
        />
      ))}

      <p style={{ fontSize: '0.6875rem', color: 'var(--dim)', margin: 0 }}>
        Estimations indicatives — à valider avec ton comptable.
      </p>
    </div>
  )
}
